/**
 * AnomalyCardItem — one card in the feed. Shows priority, category, title, money impact,
 * a "▶ видеофрагмент" button, linked Orees context, and the two manager actions.
 *
 * Framing (TZ §9): the card describes a situation to manage, not a person to punish.
 * Actions are "Подтвердить" (я вижу / беру в работу) and "Решено" (situation handled).
 */
import { useState } from "react";
import type { AnomalyCard } from "@/types/contract";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { PlayIcon, CheckIcon, CheckCircleIcon } from "@/components/ui/icons";
import { PriorityIndicator } from "@/components/anomaly/PriorityIndicator";
import { LinkedData } from "@/components/anomaly/LinkedData";
import { CATEGORY_LABELS } from "@/lib/anomaly";
import { cn, formatTenge, timeAgo } from "@/lib/utils";

interface Props {
  card: AnomalyCard;
  /** True for cards that arrived live over the channel — animate-in at the top. */
  isNew?: boolean;
  onOpenClip: (card: AnomalyCard) => void;
  onAcknowledge: (card: AnomalyCard) => void;
  onResolve: (card: AnomalyCard) => void;
  busy?: boolean;
}

export function AnomalyCardItem({
  card,
  isNew,
  onOpenClip,
  onAcknowledge,
  onResolve,
  busy,
}: Props) {
  const [showContext, setShowContext] = useState(false);
  const hasContext = Object.keys(card.linked_data).length > 0;

  return (
    <Card
      className={cn(
        "flex flex-col gap-3",
        isNew && "animate-card-in ring-1 ring-accent/40",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <PriorityIndicator priority={card.priority} />
          <Badge tone="neutral">{CATEGORY_LABELS[card.category]}</Badge>
          {card.money_impact !== null && (
            <Badge tone="warning">{formatTenge(card.money_impact)}</Badge>
          )}
        </div>
        <StatusBadge status={card.status} />
      </div>

      <div>
        <h3 className="text-base font-semibold text-ink">{card.title}</h3>
        <p className="tabular mt-0.5 text-xs text-muted">
          {timeAgo(card.created_at)} · событие {card.event_id}
        </p>
      </div>

      {hasContext && (
        <div className="rounded-control bg-surface-2/60 p-3">
          <button
            type="button"
            onClick={() => setShowContext((v) => !v)}
            className="focus-ring eyebrow mb-2 flex w-full items-center justify-between"
            aria-expanded={showContext}
          >
            <span>Сшитые данные Orees</span>
            <span className="text-muted">{showContext ? "скрыть" : "показать"}</span>
          </button>
          {showContext && <LinkedData data={card.linked_data} />}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onOpenClip(card)}
          disabled={!card.clip_ref}
          title={card.clip_ref ? undefined : "Клип к этой аномалии не прикреплён"}
        >
          <PlayIcon size={14} />
          видеофрагмент
        </Button>

        <div className="ml-auto flex items-center gap-2">
          {card.status === "open" && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onAcknowledge(card)}
              disabled={busy}
            >
              <CheckIcon size={14} />
              Подтвердить
            </Button>
          )}
          {card.status !== "resolved" && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onResolve(card)}
              disabled={busy}
            >
              <CheckCircleIcon size={14} />
              Решено
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
